import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Plus, LayoutGrid, Table2, GripVertical, Loader2, Pencil, Trash2,
  RotateCcw, RefreshCw, X, ChevronDown, ChevronUp,
} from 'lucide-react'
import { api } from '../../../services/api'
import ConfirmModal from '../../ui/ConfirmModal'
import type { Deal, DealStage, DealsSummary } from './fiTypes'
import { DEAL_STAGES, OPEN_STAGES, STAGE_LABEL } from './fiTypes'
import { formatCurrency, formatCompact } from './fiFormat'
import DealForm from './DealForm'
import ConvertDealModal from './ConvertDealModal'

const DEAL_TYPE_LABEL: Record<Deal['dealType'], string> = {
  managed: 'Gestionado',
  professional: 'Profesional',
  hybrid: 'Hibrido',
}

type ViewMode = 'kanban' | 'table'

export default function PipelineBoard() {
  const [deals, setDeals] = useState<Deal[]>([])
  const [deletedDeals, setDeletedDeals] = useState<Deal[]>([])
  const [summary, setSummary] = useState<DealsSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [view, setView] = useState<ViewMode>('kanban')
  const [showDeleted, setShowDeleted] = useState(false)
  const [loadingDeleted, setLoadingDeleted] = useState(false)

  const [formOpen, setFormOpen] = useState(false)
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null)
  const [convertTarget, setConvertTarget] = useState<Deal | null>(null)

  const [dragId, setDragId] = useState<string | null>(null)
  const [movingId, setMovingId] = useState<string | null>(null)

  // Inline capture of a lost-reason before committing a move to 'lost'.
  const [pendingLostDeal, setPendingLostDeal] = useState<Deal | null>(null)
  const [lostReasonDraft, setLostReasonDraft] = useState('')

  const [deleteTarget, setDeleteTarget] = useState<Deal | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    const [dealsRes, summaryRes] = await Promise.all([
      api.listDeals(),
      api.getDealsSummary(),
    ])
    if (dealsRes.success && dealsRes.data) setDeals(dealsRes.data.data)
    else setError(dealsRes.error || 'Error al cargar el pipeline')
    if (summaryRes.success && summaryRes.data) setSummary(summaryRes.data.data)
    setLoading(false)
  }, [])

  useEffect(() => { fetchAll() }, [fetchAll])

  // "Deleted" deals are derived by diffing an includeDeleted fetch against
  // the active list — the Deal contract doesn't expose deletedAt, so this
  // is the only reliable way to know which rows were soft-deleted.
  const loadDeleted = useCallback(async () => {
    setLoadingDeleted(true)
    const res = await api.listDeals({ includeDeleted: true })
    if (res.success && res.data) {
      const activeIds = new Set(deals.map(d => d.id))
      setDeletedDeals(res.data.data.filter(d => !activeIds.has(d.id)))
    }
    setLoadingDeleted(false)
  }, [deals])

  useEffect(() => {
    if (showDeleted) loadDeleted()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted])

  const openStageDeals = useMemo(() => {
    const byStage: Record<string, Deal[]> = {}
    for (const stage of OPEN_STAGES) byStage[stage] = []
    for (const d of deals) {
      if (byStage[d.stage]) byStage[d.stage].push(d)
    }
    return byStage
  }, [deals])

  const wonLostHistory = useMemo(() => {
    return deals
      .filter(d => d.stage === 'won' || d.stage === 'lost')
      .sort((a, b) => {
        const aDate = a.wonAt || a.lostAt || a.updatedAt
        const bDate = b.wonAt || b.lostAt || b.updatedAt
        return new Date(bDate).getTime() - new Date(aDate).getTime()
      })
      .slice(0, 8)
  }, [deals])

  const summaryByStage = useCallback((stage: DealStage) => {
    return summary?.byStage.find(s => s.stage === stage) || null
  }, [summary])

  const performStageChange = async (deal: Deal, newStage: DealStage, reason: string | null) => {
    setMovingId(deal.id)
    const res = await api.updateDealStage(deal.id, { stage: newStage, lostReason: reason })
    setMovingId(null)
    if (!res.success) {
      setError(res.error || 'Error al cambiar de etapa')
      return
    }
    await fetchAll()
    if (newStage === 'won') {
      setConvertTarget({ ...deal, stage: 'won' })
    }
  }

  const moveDeal = (deal: Deal, newStage: DealStage) => {
    if (newStage === deal.stage) return
    if (newStage === 'lost') {
      setLostReasonDraft('')
      setPendingLostDeal(deal)
      return
    }
    performStageChange(deal, newStage, null)
  }

  const handleDrop = (stage: DealStage) => (e: React.DragEvent) => {
    e.preventDefault()
    const id = e.dataTransfer.getData('text/plain')
    const deal = deals.find(d => d.id === id)
    setDragId(null)
    if (deal) moveDeal(deal, stage)
  }

  const handleDelete = async () => {
    if (!deleteTarget) return
    setDeleteBusy(true)
    const res = await api.deleteDeal(deleteTarget.id)
    setDeleteBusy(false)
    if (!res.success) {
      setError(res.error || 'Error al eliminar el deal')
      return
    }
    setDeleteTarget(null)
    await fetchAll()
    if (showDeleted) loadDeleted()
  }

  const handleRestore = async (deal: Deal) => {
    const res = await api.restoreDeal(deal.id)
    if (!res.success) {
      setError(res.error || 'Error al restaurar el deal')
      return
    }
    setDeletedDeals(prev => prev.filter(d => d.id !== deal.id))
    await fetchAll()
  }

  const openConvert = (deal: Deal) => setConvertTarget(deal)

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-severity-critical/20 border border-severity-critical text-severity-critical px-4 py-3 rounded-lg flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-severity-critical hover:opacity-70">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold text-white">Pipeline de Ventas</h2>
          <p className="text-sm text-gray-400">
            Deals en curso — valores proyectados, no facturacion realizada.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center bg-gray-800 border border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setView('kanban')}
              className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                view === 'kanban' ? 'bg-accent text-white' : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <LayoutGrid className="w-4 h-4" /> Kanban
            </button>
            <button
              onClick={() => setView('table')}
              className={`px-3 py-2 flex items-center gap-1.5 text-sm transition-colors ${
                view === 'table' ? 'bg-accent text-white' : 'text-gray-300 hover:bg-gray-700'
              }`}
            >
              <Table2 className="w-4 h-4" /> Tabla
            </button>
          </div>
          <button
            onClick={() => { setEditingDeal(null); setFormOpen(true) }}
            className="bg-accent hover:bg-accent-hover text-white font-semibold py-2 px-4 rounded-lg transition-colors flex items-center gap-2"
          >
            <Plus className="w-4 h-4" /> Nuevo Deal
          </button>
        </div>
      </div>

      {/* Pipeline summary strip */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryTile label="Deals Abiertos" value={String(summary.openCount)} />
          <SummaryTile
            label="MRR Ponderado"
            value={formatCurrency(summary.weightedPipelineMrr)}
            dashed
          />
          <SummaryTile label="Ganados este mes" value={String(summary.wonThisMonth)} tone="green" />
          <SummaryTile label="Perdidos este mes" value={String(summary.lostThisMonth)} tone="red" />
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-16 text-gray-400 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Cargando pipeline...
        </div>
      ) : view === 'kanban' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {OPEN_STAGES.map(stage => {
            const stageSummary = summaryByStage(stage)
            return (
              <div
                key={stage}
                onDragOver={e => e.preventDefault()}
                onDrop={handleDrop(stage)}
                className="bg-gray-800/50 border border-gray-700 rounded-xl flex flex-col min-h-[200px]"
              >
                <div className="p-4 border-b border-gray-700 flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-white">{STAGE_LABEL[stage]}</h3>
                    <p className="text-xs text-gray-500">
                      {stageSummary?.count ?? openStageDeals[stage]?.length ?? 0} deals
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-sm font-medium text-gray-300">
                      {formatCompact(stageSummary?.weightedMrr ?? 0)}
                    </div>
                    <div className="text-[11px] text-gray-500">ponderado/mes</div>
                  </div>
                </div>
                <div className="p-3 space-y-3 flex-1">
                  {(openStageDeals[stage] || []).length === 0 ? (
                    <p className="text-xs text-gray-500 text-center py-6">Sin deals en esta etapa</p>
                  ) : (
                    openStageDeals[stage].map(deal => (
                      <DealCard
                        key={deal.id}
                        deal={deal}
                        dragging={dragId === deal.id}
                        moving={movingId === deal.id}
                        onDragStart={() => setDragId(deal.id)}
                        onDragEnd={() => setDragId(null)}
                        onEdit={() => { setEditingDeal(deal); setFormOpen(true) }}
                        onMove={newStage => moveDeal(deal, newStage)}
                      />
                    ))
                  )}
                </div>
              </div>
            )
          })}
        </div>
      ) : (
        <TableView
          deals={deals}
          onEdit={d => { setEditingDeal(d); setFormOpen(true) }}
          onDelete={d => setDeleteTarget(d)}
          onConvert={openConvert}
        />
      )}

      {/* Won / Lost history strip */}
      <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-4">
        <h3 className="font-semibold text-white mb-3">Historial reciente (Ganados / Perdidos)</h3>
        {wonLostHistory.length === 0 ? (
          <p className="text-sm text-gray-500">Todavia no hay deals cerrados.</p>
        ) : (
          <div className="flex flex-wrap gap-3">
            {wonLostHistory.map(deal => (
              <div
                key={deal.id}
                className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-3 ${
                  deal.stage === 'won'
                    ? 'border-green-500/30 bg-green-500/10'
                    : 'border-red-500/30 bg-red-500/10'
                }`}
              >
                <div>
                  <div className="text-white font-medium">{deal.name}</div>
                  <div className="text-xs text-gray-400">
                    {deal.stage === 'won' ? 'Ganado' : 'Perdido'} ·{' '}
                    {new Date(deal.wonAt || deal.lostAt || deal.updatedAt).toLocaleDateString('es-CL')}
                    {deal.stage === 'lost' && deal.lostReason ? ` · ${deal.lostReason}` : ''}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-sm text-gray-200">{formatCurrency(deal.projectedMrr, deal.currency)}</div>
                  {deal.stage === 'won' && !deal.convertedTenantId && !deal.convertedClientId && (
                    <button
                      onClick={() => openConvert(deal)}
                      className="text-xs text-accent hover:text-accent-hover flex items-center gap-1 mt-1"
                    >
                      <RefreshCw className="w-3 h-3" /> Convertir
                    </button>
                  )}
                  {deal.stage === 'won' && (deal.convertedTenantId || deal.convertedClientId) && (
                    <div className="text-xs text-green-400 mt-1">Convertido</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Deleted deals (derived diff, expandable) */}
      <div className="bg-gray-800/30 border border-gray-800 rounded-xl">
        <button
          onClick={() => setShowDeleted(v => !v)}
          className="w-full p-4 flex items-center justify-between text-sm text-gray-400 hover:text-gray-200"
        >
          <span>Deals eliminados</span>
          {showDeleted ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
        {showDeleted && (
          <div className="p-4 pt-0 space-y-2">
            {loadingDeleted ? (
              <div className="flex items-center gap-2 text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 animate-spin" /> Cargando...
              </div>
            ) : deletedDeals.length === 0 ? (
              <p className="text-sm text-gray-500">No hay deals eliminados.</p>
            ) : (
              deletedDeals.map(deal => (
                <div key={deal.id} className="flex items-center justify-between px-3 py-2 rounded-lg bg-gray-800/50 border border-gray-700">
                  <div>
                    <span className="text-gray-300 text-sm">{deal.name}</span>
                    <span className="text-xs text-gray-500 ml-2">{STAGE_LABEL[deal.stage]}</span>
                  </div>
                  <button
                    onClick={() => handleRestore(deal)}
                    className="text-xs text-accent hover:text-accent-hover flex items-center gap-1"
                  >
                    <RotateCcw className="w-3.5 h-3.5" /> Restaurar
                  </button>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {formOpen && (
        <DealForm
          deal={editingDeal}
          onClose={() => setFormOpen(false)}
          onSaved={fetchAll}
        />
      )}

      {convertTarget && (
        <ConvertDealModal
          deal={convertTarget}
          onClose={() => setConvertTarget(null)}
          onConverted={fetchAll}
        />
      )}

      {pendingLostDeal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl max-w-md w-full border border-gray-700 p-6 space-y-4">
            <h3 className="text-lg font-bold text-white">Marcar como Perdido</h3>
            <p className="text-sm text-gray-400">{pendingLostDeal.name}</p>
            <textarea
              value={lostReasonDraft}
              onChange={e => setLostReasonDraft(e.target.value)}
              rows={3}
              autoFocus
              placeholder="Motivo (opcional): precio, timing, competencia..."
              className="w-full bg-gray-700 border border-gray-700 rounded-lg px-4 py-2 text-white focus:outline-none focus:ring-2 focus:ring-accent resize-none"
            />
            <div className="flex items-center justify-end gap-3">
              <button
                onClick={() => setPendingLostDeal(null)}
                className="px-4 py-2 text-gray-300 hover:text-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  const deal = pendingLostDeal
                  setPendingLostDeal(null)
                  performStageChange(deal, 'lost', lostReasonDraft || null)
                }}
                className="bg-red-600 hover:bg-red-500 text-white font-semibold py-2 px-4 rounded-lg transition-colors"
              >
                Marcar como Perdido
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        open={!!deleteTarget}
        title="Eliminar Deal"
        message={`¿Eliminar "${deleteTarget?.name}"? Podras restaurarlo despues.`}
        variant="danger"
        confirmLabel="Eliminar"
        busy={deleteBusy}
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  )
}

function SummaryTile({ label, value, dashed, tone }: {
  label: string; value: string; dashed?: boolean; tone?: 'green' | 'red'
}) {
  const toneClass = tone === 'green' ? 'text-green-400' : tone === 'red' ? 'text-red-400' : 'text-white'
  return (
    <div className={`bg-gray-800/50 border rounded-xl p-4 ${dashed ? 'border-dashed border-gray-600' : 'border-gray-700'}`}>
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-xl font-bold ${toneClass}`}>{value}</div>
      {dashed && <div className="text-[11px] text-gray-500 mt-1">Proyectado — no facturado</div>}
    </div>
  )
}

function DealCard({ deal, dragging, moving, onDragStart, onDragEnd, onEdit, onMove }: {
  deal: Deal
  dragging: boolean
  moving: boolean
  onDragStart: () => void
  onDragEnd: () => void
  onEdit: () => void
  onMove: (stage: DealStage) => void
}) {
  return (
    <div
      draggable
      onDragStart={e => { e.dataTransfer.setData('text/plain', deal.id); onDragStart() }}
      onDragEnd={onDragEnd}
      className={`border-2 border-dashed rounded-lg p-3 bg-gray-900/60 transition-opacity ${
        dragging ? 'opacity-40' : 'opacity-100'
      } border-gray-600 hover:border-accent/60`}
    >
      <div className="flex items-start justify-between gap-2">
        <button onClick={onEdit} className="text-left flex-1 min-w-0">
          <div className="flex items-center gap-1.5 text-white font-medium text-sm truncate">
            <GripVertical className="w-3.5 h-3.5 text-gray-600 flex-shrink-0" />
            <span className="truncate">{deal.name}</span>
          </div>
        </button>
        {moving && <Loader2 className="w-3.5 h-3.5 animate-spin text-accent flex-shrink-0" />}
      </div>

      <div className="flex items-center gap-2 mt-2 flex-wrap">
        <span className="text-[11px] px-1.5 py-0.5 rounded bg-gray-700 text-gray-300">
          {DEAL_TYPE_LABEL[deal.dealType]}
        </span>
        <span className={`text-[11px] px-1.5 py-0.5 rounded ${
          deal.isIntragroup ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
        }`}>
          {deal.isIntragroup ? 'Intra' : 'Externo'}
        </span>
      </div>

      <div className="mt-2 flex items-center justify-between">
        <span className="text-gray-300 text-sm">{formatCurrency(deal.projectedMrr, deal.currency)}/mes</span>
        <span className="text-xs text-gray-500">{deal.probability}%</span>
      </div>

      <select
        value={deal.stage}
        onChange={e => onMove(e.target.value as DealStage)}
        className="mt-2 w-full text-xs bg-gray-800 border border-gray-700 rounded px-2 py-1 text-gray-300 focus:outline-none focus:ring-1 focus:ring-accent"
        title="Mover a otra etapa"
      >
        {DEAL_STAGES.map(s => (
          <option key={s} value={s}>{STAGE_LABEL[s]}</option>
        ))}
      </select>
    </div>
  )
}

function TableView({ deals, onEdit, onDelete, onConvert }: {
  deals: Deal[]
  onEdit: (d: Deal) => void
  onDelete: (d: Deal) => void
  onConvert: (d: Deal) => void
}) {
  return (
    <div className="bg-gray-800/50 border border-gray-700 rounded-xl overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-700 text-left text-gray-400">
            <th className="p-3 font-medium">Nombre</th>
            <th className="p-3 font-medium">Tipo</th>
            <th className="p-3 font-medium">Etapa</th>
            <th className="p-3 font-medium">MRR Proy.</th>
            <th className="p-3 font-medium">Prob.</th>
            <th className="p-3 font-medium">Ponderado</th>
            <th className="p-3 font-medium">Cierre Est.</th>
            <th className="p-3 font-medium">Grupo</th>
            <th className="p-3 font-medium text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {deals.length === 0 && (
            <tr><td colSpan={9} className="p-6 text-center text-gray-500">No hay deals registrados.</td></tr>
          )}
          {deals.map(deal => (
            <tr key={deal.id} className="border-b border-gray-800 hover:bg-gray-800/40">
              <td className="p-3 text-white">{deal.name}</td>
              <td className="p-3 text-gray-300">{DEAL_TYPE_LABEL[deal.dealType]}</td>
              <td className="p-3">
                <span className={`text-xs px-2 py-0.5 rounded ${
                  deal.stage === 'won' ? 'bg-green-500/20 text-green-300'
                    : deal.stage === 'lost' ? 'bg-red-500/20 text-red-300'
                    : 'bg-gray-700 text-gray-300'
                }`}>
                  {STAGE_LABEL[deal.stage]}
                </span>
              </td>
              <td className="p-3 text-gray-300">{formatCurrency(deal.projectedMrr, deal.currency)}</td>
              <td className="p-3 text-gray-400">{deal.probability}%</td>
              <td className="p-3 text-gray-400">
                {formatCurrency(deal.projectedMrr * (deal.probability / 100), deal.currency)}
              </td>
              <td className="p-3 text-gray-400">
                {deal.expectedCloseDate ? new Date(deal.expectedCloseDate).toLocaleDateString('es-CL') : '—'}
              </td>
              <td className="p-3">
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  deal.isIntragroup ? 'bg-purple-500/20 text-purple-300' : 'bg-blue-500/20 text-blue-300'
                }`}>
                  {deal.isIntragroup ? 'Intra' : 'Externo'}
                </span>
              </td>
              <td className="p-3">
                <div className="flex items-center justify-end gap-2">
                  <button onClick={() => onEdit(deal)} title="Editar" className="text-gray-400 hover:text-white">
                    <Pencil className="w-4 h-4" />
                  </button>
                  {deal.stage === 'won' && !deal.convertedTenantId && !deal.convertedClientId && (
                    <button onClick={() => onConvert(deal)} title="Convertir" className="text-accent hover:text-accent-hover">
                      <RefreshCw className="w-4 h-4" />
                    </button>
                  )}
                  <button onClick={() => onDelete(deal)} title="Eliminar" className="text-gray-400 hover:text-severity-critical">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
